import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import './FeedbackQueue.css';

function FeedbackQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchList = async () => {
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/feedback`);
      setItems(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load feedback queue');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.patch(`${API_BASE_URL}/api/feedback/${id}`, { status });
      setItems((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status } : row))
      );
    } catch (err) {
      alert(err.response?.data?.error || 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  if (loading) {
    return (
      <div className="feedback-queue card">
        <p>Loading feedback queue…</p>
      </div>
    );
  }

  return (
    <div className="feedback-queue card">
      <div className="feedback-queue-header">
        <h2>Feedback queue</h2>
        <button type="button" className="btn btn-secondary" onClick={fetchList}>
          Refresh
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}
      {items.length === 0 && !error ? (
        <p className="feedback-queue-empty">No reports yet.</p>
      ) : (
        <div className="table-container">
          <table className="feedback-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User (login)</th>
                <th>Type</th>
                <th>Title</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>{row.reporterEmail}</td>
                    <td>{row.category}</td>
                    <td>{row.title}</td>
                    <td>
                      <select
                        className="feedback-status-select"
                        value={row.status}
                        disabled={updatingId === row.id}
                        onChange={(e) => updateStatus(row.id, e.target.value)}
                        aria-label={`Status for ${row.title}`}
                      >
                        <option value="new">new</option>
                        <option value="triaged">triaged</option>
                        <option value="closed">closed</option>
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() =>
                          setExpandedId((id) => (id === row.id ? null : row.id))
                        }
                      >
                        {expandedId === row.id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr className="feedback-detail-row">
                      <td colSpan={6}>
                        <div className="feedback-detail">
                          <strong>Name:</strong> {row.reporterName}
                          <br />
                          <strong>Report ID:</strong> {row.id}
                          <pre className="feedback-body">{row.body}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FeedbackQueue;
